import { useEffect, useState, useRef, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import WTMonogram from '../components/WTMonogram';
import { PlayerAvatar } from '../components/PlayerAvatar';
import AuthLegalFooter from '../components/AuthLegalFooter';
import '../styles/login.css';
import { BACKEND_URL } from '../config';
import type { Session } from '../types/app';

interface MagicPseudoOption {
  pseudo: string;
  pseudo_display: string;
  has_photo: boolean;
}

interface ResolveResponse {
  email: string;
  pseudos: MagicPseudoOption[];
  pseudo_count: number;
  expires_at: string;
}

interface ConfirmResponse {
  success: boolean;
  token: string;
  hash_pass: string;
  user: { pseudo: string; pseudo_display: string; method: string };
}

interface MagicLinkCallbackProps {
  onLogin: (session: Session) => void;
}

export default function MagicLinkCallback({ onLogin }: MagicLinkCallbackProps) {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pseudos, setPseudos] = useState<MagicPseudoOption[]>([]);
  const [email, setEmail] = useState('');
  const [confirming, setConfirming] = useState<string | null>(null);
  const autoLoginAttempted = useRef(false);

  // Résolution du token au montage
  useEffect(() => {
    if (!token) {
      setLoading(false);
      setError('Lien de connexion invalide.');
      return;
    }

    let cancelled = false;
    setLoading(true);

    fetch(`${BACKEND_URL}/api/auth/magic-resolve`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    })
      .then(async (res) => {
        const data = await res.json();
        if (cancelled) return;
        if (!res.ok) {
          setError(data.message || 'Lien invalide ou expiré.');
          return;
        }
        const resolved = data as ResolveResponse;
        setEmail(resolved.email || '');
        setPseudos(resolved.pseudos || []);
      })
      .catch(() => {
        if (!cancelled) setError('Erreur de communication avec le serveur.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [token]);

  const confirmPseudo = useCallback(
    async (pseudo: string) => {
      setError('');
      setConfirming(pseudo);
      try {
        const res = await fetch(`${BACKEND_URL}/api/auth/magic-confirm`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token, pseudo }),
        });
        const data = await res.json();
        if (!res.ok) {
          throw new Error(data.message || 'Connexion impossible.');
        }
        const result = data as ConfirmResponse;
        onLogin({
          pseudo: result.user.pseudo,
          pseudoDisplay: result.user.pseudo_display || result.user.pseudo,
          compet: false,
          token: result.token,
          hashPass: result.hash_pass,
        });
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Connexion impossible.');
        setConfirming(null);
      }
    },
    [token, onLogin],
  );

  // Auto-login si un seul pseudo correspond
  useEffect(() => {
    if (loading || error || autoLoginAttempted.current) return;
    if (pseudos.length === 1) {
      autoLoginAttempted.current = true;
      confirmPseudo(pseudos[0].pseudo);
    }
  }, [loading, error, pseudos, confirmPseudo]);

  return (
    <div className="login-page">
      <div className="login-bg" />
      <div className="login-card login-card-wide">
        <div className="login-header">
          <WTMonogram />
          <h1 className="login-title">Webtarot</h1>
          <p className="login-tagline">
            {loading
              ? 'Vérification du lien…'
              : error
              ? 'Lien de connexion'
              : pseudos.length > 1
              ? 'Choisissez votre pseudo'
              : 'Connexion en cours…'}
          </p>
        </div>

        <div className="login-body">
          {loading && (
            <div className="oauth-callback-content">
              <div className="loading-spinner" />
              <p>Vérification du lien magique…</p>
            </div>
          )}

          {!loading && error && (
            <>
              <div className="login-error">{error}</div>
              <div className="login-links">
                <Link to="/auth/magic-link">Demander un nouveau lien</Link>
              </div>
            </>
          )}

          {!loading && !error && pseudos.length > 1 && (
            <>
              <div className="oauth-welcome">
                <p>
                  Plusieurs pseudos sont associés à <strong>{email}</strong>.
                  Sélectionnez celui que vous souhaitez utiliser.
                </p>
              </div>

              <ul className="oauth-pseudo-list" role="listbox" aria-label="Pseudos disponibles">
                {pseudos.map((item) => {
                  const isBusy = confirming === item.pseudo;
                  return (
                    <li key={item.pseudo}>
                      <button
                        type="button"
                        className="oauth-pseudo-card"
                        onClick={() => confirmPseudo(item.pseudo)}
                        disabled={Boolean(confirming)}
                        role="option"
                      >
                        <PlayerAvatar pseudo={item.pseudo} size={56} />
                        <div className="oauth-pseudo-card-text">
                          <span className="oauth-pseudo-card-name">
                            {item.pseudo_display}
                          </span>
                        </div>
                        <span className="oauth-pseudo-card-action" aria-hidden="true">
                          {isBusy ? '…' : '→'}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>

              <p className="oauth-select-hint">
                Lien à usage unique. Si vous fermez cette page sans choisir,
                vous devrez en demander un nouveau.
              </p>
            </>
          )}

          {!loading && !error && pseudos.length === 1 && (
            <div className="oauth-callback-content">
              <div className="loading-spinner" />
              <p>Connexion à {pseudos[0].pseudo_display}…</p>
            </div>
          )}

          {!loading && !error && (
            <div className="login-links">
              <Link to="/">Annuler</Link>
            </div>
          )}

          <AuthLegalFooter />
        </div>
      </div>
    </div>
  );
}
